// ═══════════════════════════════════════════════════════════════════
//  modules/luckperms/LpMatrice.js
//  Le tableau : une ligne par permission, une colonne par grade.
//  Un clic fait tourner la case entre les trois états.
// ═══════════════════════════════════════════════════════════════════

import { useState } from "react";
import { THEME } from "../../socle/theme";
import { symbole, libelleValeur } from "./api";

// Au-dela de ce nombre de lignes, un domaine s'affiche replie. Le dump du
// plugin fait entrer plusieurs milliers de permissions d'un coup — surtout
// FastAsyncWorldEdit et les minecraft.command.* de Paper. Les dessiner
// toutes rendrait la page inutilisable, et personne n'arbitre 2 000
// permissions en les faisant defiler : on les cherche.
const SEUIL_REPLI = 60;

const COUL = {
  true:  { fond: THEME.emeraldDim, bord: THEME.emerald, txt: THEME.emerald },
  false: { fond: THEME.redDim,     bord: THEME.red,     txt: THEME.red },
  unset: { fond: "transparent",    bord: THEME.border,  txt: THEME.textDim },
};

function Case({ valeur, herite, modifie, jamais, petit, onClick, titre }) {
  const c = COUL[valeur] || COUL.unset;
  const taille = petit ? 26 : 32;
  return (
    <button
      type="button"
      onClick={jamais ? undefined : onClick}
      title={titre}
      style={{
        width: taille, height: taille, borderRadius: 8, padding: 0,
        cursor: jamais ? "not-allowed" : "pointer",
        background: herite && !modifie ? "transparent" : c.fond,
        border: `1.5px solid ${modifie ? THEME.accent : c.bord}`,
        color: c.txt, fontSize: petit ? 12 : 14, fontWeight: 700, lineHeight: 1,
        opacity: jamais ? 0.5 : herite && !modifie ? 0.45 : 1,
        boxShadow: modifie ? `0 0 0 2px ${THEME.accentDim}` : "none",
        transition: ".1s",
      }}
    >
      {jamais ? "✕" : symbole(valeur)}
    </button>
  );
}

export function LpMatrice({
  grades, catalogue, etat, brouillon, onBasculer, onProposer, filtre, isMobile,
}) {
  const f = (filtre || "").trim().toLowerCase();
  const visibles = catalogue.filter(
    (c) => !f ||
      c.node.toLowerCase().includes(f) ||
      (c.description || "").toLowerCase().includes(f) ||
      (c.plugin || "").toLowerCase().includes(f) ||
      (c.categorie || "").toLowerCase().includes(f)
  );

  if (!visibles.length)
    return (
      <div style={{ color: THEME.textDim, padding: 30, textAlign: "center" }}>
        Aucune permission ne correspond à « {filtre} ».
      </div>
    );

  const categories = [];
  for (const c of visibles) {
    let g = categories.find((x) => x.nom === c.categorie);
    if (!g) { g = { nom: c.categorie, lignes: [] }; categories.push(g); }
    g.lignes.push(c);
  }

  return (
    <div>
      {categories.map((cat) => (
        <Domaine
          key={cat.nom} cat={cat} grades={grades} etat={etat} brouillon={brouillon}
          onBasculer={onBasculer} onProposer={onProposer} isMobile={isMobile}
          filtre={f}
        />
      ))}
    </div>
  );
}

function Domaine({ cat, grades, etat, brouillon, onBasculer, onProposer, isMobile, filtre }) {
  // Un domaine trop gros s'ouvre replie — sauf si un filtre est actif :
  // dans ce cas l'utilisateur a deja restreint ce qu'il veut voir.
  const grosDomaine = cat.lignes.length > SEUIL_REPLI && !filtre;
  const [ouvert, setOuvert] = useState(!grosDomaine);

  return (
        <div style={{ marginBottom: 26 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
            <div style={{ color: THEME.accent, fontSize: 15, fontWeight: 650 }}>{cat.nom}</div>
            <span style={{ color: THEME.textDim, fontSize: 12.5 }}>
              {cat.lignes.length} permission{cat.lignes.length > 1 ? "s" : ""}
            </span>
            {ouvert && (
              <button
                type="button"
                onClick={() => onProposer(cat.lignes)}
                style={{
                  background: "transparent", border: `1px solid ${THEME.border}`,
                  color: THEME.textMuted, borderRadius: 6, padding: "3px 9px",
                  fontSize: 11.5, cursor: "pointer",
                }}
              >
                appliquer la proposition du 1er août
              </button>
            )}
            <button
              type="button"
              onClick={() => setOuvert(!ouvert)}
              style={{
                background: ouvert ? "transparent" : THEME.accent,
                border: `1px solid ${ouvert ? THEME.border : THEME.accent}`,
                color: ouvert ? THEME.textMuted : "#fff",
                borderRadius: 6, padding: "3px 9px", fontSize: 11.5, cursor: "pointer",
              }}
            >
              {ouvert ? "replier" : "afficher"}
            </button>
          </div>

          {!ouvert && (
            <div style={{
              background: THEME.panel, border: `1px solid ${THEME.border}`,
              borderRadius: 8, padding: "12px 14px", color: THEME.textMuted, fontSize: 13,
            }}>
              Replié : {cat.lignes.length} permissions, trop pour être parcourues.
              Sers-toi du filtre en haut pour trouver celle que tu cherches.
            </div>
          )}

          {ouvert && (

          <div style={{
            background: THEME.panel, border: `1px solid ${THEME.border}`,
            borderRadius: 8, overflow: "hidden",
          }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th(isMobile ? undefined : 300)}>Permission</th>
                  {!isMobile && <th style={th()}>Ce que ça autorise</th>}
                  {grades.map((g) => (
                    <th key={g.cle} style={{ ...th(isMobile ? 34 : 78), textAlign: "center", padding: "9px 2px" }}>
                      {isMobile ? g.libelle.slice(0, 3) : g.libelle}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cat.lignes.map((c) => {
                  const reel = etat[c.node] || {};
                  const modif = brouillon[c.node] || {};
                  return (
                    <tr key={c.node}>
                      <td style={td()}>
                        <div style={{
                          fontFamily: "ui-monospace, Consolas, monospace",
                          fontSize: 12.5, color: THEME.accent, wordBreak: "break-all",
                        }}>
                          {c.node}
                        </div>
                        <div style={{ color: THEME.textDim, fontSize: 11, marginTop: 2 }}>
                          {c.plugin}
                          {c.serveurs && c.serveurs.split(",").length === 1 ? ` · ${c.serveurs} seulement` : ""}
                          {c.source === "dump" ? " · détecté" : ""}
                        </div>
                        {isMobile && (
                          <>
                            <div style={{ color: THEME.text, fontSize: 12, marginTop: 4 }}>{c.description}</div>
                            {c.remarque ? <Remarque texte={c.remarque} /> : null}
                          </>
                        )}
                      </td>

                      {!isMobile && (
                        <td style={td()}>
                          <div style={{ color: THEME.text, fontSize: 13 }}>{c.description}</div>
                          {c.remarque ? <Remarque texte={c.remarque} /> : null}
                        </td>
                      )}

                      {grades.map((g) => {
                        const e = reel[g.cle] || { v: "unset", h: null };
                        const enCours = modif[g.cle];
                        const valeur = enCours !== undefined ? enCours : e.v;
                        const herite = !!e.h;
                        return (
                          <td key={g.cle} style={{ ...td(), textAlign: "center", padding: "6px 2px" }}>
                            <Case
                              valeur={valeur}
                              herite={herite}
                              modifie={enCours !== undefined}
                              jamais={c.jamais}
                              petit={isMobile}
                              onClick={() => onBasculer(c.node, g.cle, valeur)}
                              titre={
                                c.jamais
                                  ? "Joker : jamais applicable"
                                  : `${g.libelle} — ${libelleValeur(valeur)}` +
                                    (herite && enCours === undefined ? ` (hérité de ${e.h})` : "") +
                                    (enCours !== undefined ? ` · sera changé (était : ${libelleValeur(e.v)})` : "")
                              }
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
  );
}

function Remarque({ texte }) {
  return (
    <div style={{ color: THEME.amber, fontSize: 11.5, marginTop: 4, lineHeight: 1.5 }}>
      {texte}
    </div>
  );
}

const th = (w) => ({
  background: THEME.surface, color: THEME.textMuted, fontSize: 10.5,
  textTransform: "uppercase", letterSpacing: ".6px", fontWeight: 600,
  padding: "9px 10px", textAlign: "left", whiteSpace: "nowrap",
  borderBottom: `1px solid ${THEME.border}`, width: w || undefined,
});

const td = () => ({
  padding: "9px 10px", borderBottom: `1px solid ${THEME.border}`,
  verticalAlign: "top", fontSize: 13,
});
